<template>
  <div :class="wrapperClasses">
    <!-- Label -->
    <label v-if="label" :for="id" :class="labelClasses">
      {{ label }}
      <span v-if="required" class="text-danger ml-1">*</span>
    </label>

    <!-- Input wrapper (for icons) -->
    <div class="relative">
      <!-- Icon Left -->
      <!-- <div v-if="iconLeft" class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"> -->
      <div v-if="iconLeft || $slots['icon-left']" class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <slot name="icon-left">
          <span class="text-gray-500" v-html="iconLeft"></span>
        </slot>
      </div>

      <!-- Input element -->
      <input
        :id="id"
        class="pl-10"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :min="min"
        :max="max"
        :step="step"
        :autocomplete="autocomplete"
        :class="inputClasses"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
        @keyup.enter="handleEnter"
      />

      <!-- Icon Right -->
      <div v-if="iconRight || clearable || $slots['icon-right']" class="absolute inset-y-0 right-0 pr-3 flex items-center">
        <!-- Clear button -->
        <button
          v-if="clearable && modelValue"
          type="button"
          class="text-gray-400 hover:text-gray-600 focus:outline-none"
          @click="handleClear"
        >
          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </button>

        <!-- Icon Right slot -->
        <slot v-else name="icon-right">
          <span v-if="iconRight" class="text-gray-500" v-html="iconRight"></span>
        </slot>
      </div>
    </div>

    <!-- Helper text or error message -->
    <p v-if="error" class="mt-1 text-sm text-danger">{{ error }}</p>
    <p v-else-if="hint" class="mt-1 text-sm text-gray-600">{{ hint }}</p>
  </div>
</template>

<script>
export default {
  name: 'BaseInput',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    id: {
      type: String,
      default: () => `input-${Math.random().toString(36).substr(2, 9)}`
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => [
        'text',
        'email',
        'password',
        'number',
        'tel',
        'url',
        'search',
        'date',
        'time',
        'datetime-local'
      ].includes(value)
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    clearable: {
      type: Boolean,
      default: false
    },
    min: {
      type: [String, Number],
      default: null
    },
    max: {
      type: [String, Number],
      default: null
    },
    step: {
      type: [String, Number],
      default: null
    },
    autocomplete: {
      type: String,
      default: 'off'
    }
  },
  computed: {
    wrapperClasses() {
      return 'w-full'
    },
    labelClasses() {
      return 'block mb-1 text-sm font-medium text-gray-700'
    },
    inputClasses() {
      return [
        'block w-full border border-gray-300 rounded-md shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary-light',
        this.sizeClasses,
        this.iconLeft ? 'pl-10' : '',
        this.iconRight || this.clearable ? 'pr-10' : '',
        this.error ? 'border-danger focus:ring-red-500 focus:border-red-500' : '',
        this.disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white',
        this.readonly ? 'bg-gray-50' : ''
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-5 py-3 text-lg'
      }
      return sizes[this.size]
    }
  },
  methods: {
    handleInput(event) {
      let value = event.target.value

      // For number type, convert to number
      if (this.type === 'number' && value !== '') {
        value = Number(value)
      }

      this.$emit('update:modelValue', value)
      this.$emit('input', value)
    },
    handleBlur(event) {
      this.$emit('blur', event)
    },
    handleFocus(event) {
      this.$emit('focus', event)
    },
    handleEnter(event) {
      this.$emit('enter', event)
    },
    handleClear() {
      this.$emit('update:modelValue', '')
      this.$emit('clear')
    }
  }
}
</script>
